"use client";

import { useState, useCallback } from "react";
import { cloneDeep } from "lodash";
import { useLocale, useTranslations } from "next-intl";
import { useStateHistoryChatPage } from "@/app/store/chat";
import { useAuth } from "@/app/hooks";
import { useAiSourceSelector } from "@/app/store/ai-source-selector";
import { fetchWithAuth } from "@/app/lib/authInterceptor";
import { api_url } from "@/app/api";
import { METHOD_TYPE } from "@/app/lib/enums";
import { CHAT_MESSAGE_METADATA_TYPE } from "@/app/lib/constants";
import {
   AttachedFile,
   ChatMessage,
   ChatHistory,
   PaginatedMessages,
   SharedChatHistoryResponse
} from "@/types";

const HISTORY_REFETCH_DELAY_MS = 1_000;
const NAVIGATION_DELAY_MS = 300;

function withAttachedFiles(message: ChatMessage): ChatMessage {
   const descriptors = message.metaData?.[CHAT_MESSAGE_METADATA_TYPE.ATTACHED_FILES];
   if (!Array.isArray(descriptors) || !descriptors.length) return message;

   return {
      ...message,
      attachedFiles: descriptors.map((descriptor: AttachedFile) => ({
         ...descriptor,
         url: descriptor.restricted ? "" : api_url.file_url.replace("{id}", descriptor.id)
      }))
   };
}

export function sortMessages(data: SharedChatHistoryResponse | ChatHistory) {
   // Sort new messages chronologically (oldest to newest)
   return [...data.messages.items]
      .sort((a, b) => {
         const timeA = new Date(a.timestamp || Date.now()).getTime();
         const timeB = new Date(b.timestamp || Date.now()).getTime();
         return timeA - timeB;
      })
      .map(withAttachedFiles);
}

// The BE clamps any requested limit to MAX_MESSAGES_PER_TOPIC, so a topic that grew
// past the cap would come back as its OLDEST slice with the newest messages missing.
export async function loadAllPages<T extends { messages: PaginatedMessages }>(
   fetchPage: (page: number) => Promise<T | null>
): Promise<T | null> {
   const first = await fetchPage(1);
   if (!first?.messages?.items?.length) return first;

   const items = [...first.messages.items];
   let pageInfo = first.messages.pageInfo;
   let page = 1;

   while (pageInfo?.hasNextPage) {
      page += 1;
      const next = await fetchPage(page);
      if (!next?.messages?.items?.length) break;
      items.push(...next.messages.items);
      pageInfo = next.messages.pageInfo;
   }

   return { ...first, messages: { items, pageInfo } } as T;
}

interface UseChatHistoryLoaderProps {
   shareKey?: string;
}

export const useChatHistoryLoader = ({ shareKey }: UseChatHistoryLoaderProps) => {
   const locale = useLocale();
   const translation = useTranslations("Home");
   const { user } = useAuth();

   const [history, setHistory] = useState<ChatMessage[]>([]);
   const [ownerShareId, setOwnerShareId] = useState<string>("");

   const { setHistoryItem, selectedModel, selectedTemperature, triggerHistoryRefetch } =
      useStateHistoryChatPage();

   const { selectedBaseKnowledge } = useAiSourceSelector();

   const handleCreateNewChat = useCallback(
      async (newHistory: ChatMessage[]) => {
         const isNotCreateNewChat =
            (!shareKey && (!newHistory.length || newHistory?.[0]?.id)) || !user?.id;
         if (isNotCreateNewChat) return;

         // Caller should manage isCreatingNewChat state
         try {
            const chatHistoryClone = cloneDeep(newHistory);
            let historyIdTemp = "";
            const messageItem = chatHistoryClone[0];
            const responseData = await fetchWithAuth(api_url.chat_history_url, {
               method: METHOD_TYPE.POST,
               headers: {
                  "Content-Type": "application/json",
                  "Knowledge-Base": selectedBaseKnowledge.id
               },
               body: JSON.stringify({
                  userId: user.id,
                  message: {
                     role: messageItem.role,
                     content: messageItem.content,
                     modelName: selectedModel,
                     temperature: selectedTemperature,
                     metaData: messageItem.metaData,
                     expandedQuery: newHistory[0].expandedQuery
                  }
               })
            });
            if (!responseData.ok) {
               const errorMessage =
                  responseData.statusText || translation("unableToConnect");
               throw new Error(`${errorMessage} (ErrorCode: ${responseData.status})`);
            }
            const { data } = await responseData.json();
            if (!data || !data.id) return;
            data.id && setHistoryItem({ ...data });
            setOwnerShareId(user.id);
            chatHistoryClone[0].id = data.id;
            setHistory([...chatHistoryClone]);
            historyIdTemp = data.id;
            await handleAddToHistory(chatHistoryClone, historyIdTemp);
            setTimeout(() => {
               triggerHistoryRefetch();
               setTimeout(() => {
                  window.history.replaceState(null, "", `/${locale}/c/${historyIdTemp}`);
                  if (shareKey) {
                     window.location.reload();
                  }
               }, NAVIGATION_DELAY_MS);
            }, HISTORY_REFETCH_DELAY_MS);
         } catch (error) {
            console.error("Error creating new chat:", error);
            throw error;
         }
      },
      [
         shareKey,
         user?.id,
         selectedBaseKnowledge.id,
         selectedModel,
         selectedTemperature,
         setHistoryItem,
         triggerHistoryRefetch,
         locale,
         translation
      ]
   );

   const handleAddToHistory = useCallback(
      async (newHistory: ChatMessage[], historyChatId: string = "") => {
         if (!historyChatId || !newHistory.length) return;
         const chatHistoryClone = cloneDeep(newHistory);
         let index = 0;
         for (const message of chatHistoryClone) {
            if (shareKey && index > 0) {
               message.id = "";
               const timeOut = index % 100 === 0 ? 1_000 : 0;
               await new Promise((resolve) => setTimeout(resolve, timeOut));
            }
            if (!message.id) {
               const addMessageToHistory = await fetchWithAuth(api_url.chat_history_url, {
                  method: METHOD_TYPE.PUT,
                  headers: {
                     "Content-Type": "application/json",
                     "Knowledge-Base": selectedBaseKnowledge.id
                  },
                  body: JSON.stringify({
                     addMessageToChatHistoryId: historyChatId,
                     message: {
                        role: message.role,
                        content: message.content,
                        modelName: selectedModel,
                        temperature: selectedTemperature,
                        expandedQuery: message.expandedQuery,
                        metaData: message.metaData
                     }
                  })
               });
               if (!addMessageToHistory.ok) {
                  const errorMessage =
                     addMessageToHistory.statusText || translation("unableToConnect");
                  throw new Error(
                     `${errorMessage} (ErrorCode: ${addMessageToHistory.status})`
                  );
               }
               const { data } = await addMessageToHistory.json();
               if (!data || !data.visibleMessages?.items?.length) return;
               const messageId =
                  data.visibleMessages.items[data.visibleMessages.items.length - 1].id;
               message.id = messageId;
            }
            index++;
         }

         setHistory([...chatHistoryClone]);
      },
      [
         shareKey,
         selectedBaseKnowledge.id,
         selectedModel,
         selectedTemperature,
         translation
      ]
   );

   return {
      history,
      setHistory,
      ownerShareId,
      setOwnerShareId,
      handleCreateNewChat,
      handleAddToHistory
   };
};
